import { ControlledTextField } from '@issuemanager/apps/client/components';
import { DECLINE_REASON_LENGTH_MAX } from '@issuemanager/constants';
import { Stack, useEventCallback } from '@mui/material';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { LayoutPanel } from '../layout';

export type DeclineWithCommentBody = {
  comment?: string;
};

type FormValues = {
  comment: string;
};

export type IssueDeclineWithCommentPanelProps = {
  issueTitle: string;
  isOpen: boolean;
  onClose: () => void;
  onDecline: (body?: DeclineWithCommentBody) => void;
  isPending: boolean;
  formTestId?: string;
  commentTestId?: string;
  footerButtonTestId?: string;
};

export function IssueDeclineWithCommentPanel({
  issueTitle,
  isOpen,
  onClose,
  onDecline,
  isPending,
  formTestId = 'form-decline-with-comment',
  commentTestId = 'decline-with-comment-comment',
  footerButtonTestId = 'decline-with-comment-footer-button',
}: IssueDeclineWithCommentPanelProps) {
  const { t } = useTranslation('issue');
  const form = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: { comment: '' },
  });

  useEffect(() => {
    if (!isOpen) {
      form.reset();
    }
  }, [isOpen, form]);

  const handleSubmit = useEventCallback(() => {
    form.handleSubmit((values) => {
      onDecline(
        values.comment.trim() ? { comment: values.comment.trim() } : undefined,
      );
    })();
  });

  return (
    <LayoutPanel
      isOpen={isOpen}
      onCancel={() => onClose()}
      title={t('declineToDraft.title')}
      description={
        <Trans
          t={t}
          i18nKey='declineToDraft.description'
          components={{ b: <b /> }}
          values={{ issueTitle }}
        />
      }
      actionButton={{
        disabled: false,
        'data-testid': footerButtonTestId,
      }}
      actionButtonText={t('declineToDraft.submit')}
      isLoading={isPending}
      onSend={handleSubmit}
      content={
        <form data-testid={formTestId}>
          <Stack gap={2}>
            <ControlledTextField
              name='comment'
              label={t('declineToDraft.commentLabel')}
              placeholder={t('declineToDraft.commentPlaceholder')}
              control={form.control}
              data-testid={commentTestId}
              multiline
              minRows={4}
              maxLength={DECLINE_REASON_LENGTH_MAX}
            />
          </Stack>
        </form>
      }
    />
  );
}
